import { Box, Button, CircularProgress, Stack, Typography } from "@mui/joy";
import CustomModal from "components/common/CustomModal";
import React, { useEffect, useRef, useState } from "react";
import useGetDeviceInfo from "hooks/useGetDeviceInfo";
import useGetUrls from "hooks/useGetUrls";
import { theme } from "styles/theme";
import FloatingArea from "components/common/FloatingArea";
import { useRecoilState } from "recoil";
import { useQuery } from "@tanstack/react-query";
import {
  PAYMENT_COACH_SEAT_COUNT_ENDPOINT,
  PAYMENT_DOMAIN,
  paymentApi,
} from "api/paymentApi";
import CustomTypography from "../../common/CustomTypography";
import LinkV2 from "../../common/LinkV2";
import CheckIcon from "../../icons/CheckIcon";
import InfoIcon from "../../icons/InfoIcon";
import { userSimpleAtom } from "recoil/userSimpleAtom";
import { PATH } from "routes/paths";
import { useNavigate } from "react-router-dom";
import FAQTypeC from "../../task/payment/elements/FAQTypeC";
import { PlanItem, PlanKey } from "../../task/payment/planTypes";
import ContentTemplateV2 from "../../task/content/ContentTemplateV2";
import BackButton from "../../common/BackButton";
import PaymentButtonV3 from "./PaymentButtonV3";
import useFetchUserV3 from "./useFetchUserV3";
import useAutoLoginV3 from "./useAutoLoginV3";
import useMixpanelMKT from "../../../hooks/useMixpanelMKT";

const plans: PlanItem[] = [
  {
    key: "starter",
    name: "스타터",
    price: 295000,
    features: [
      { text: "기본 코칭 (주 최대 2회)", included: true, info: true },
      { text: "환불 페이백", included: true },
    ],
  },
  {
    key: "challenger",
    name: "챌린저",
    price: 395000,
    tag: "가장 인기 있는",
    features: [
      { text: "집중 코칭 (일 최대 1회)", included: true, info: true },
      { text: "기본 정기 검사", included: true, info: true },
      { text: "환불 페이백", included: true },
    ],
  },
  {
    key: "master",
    name: "마스터",
    price: 465000,
    tag: "가장 밀도 있는",
    features: [
      {
        text: "프리미엄 코칭 (일 최대 2회, 최우선 관리)",
        included: true,
        info: true,
      },
      { text: "기본 정기 검사", included: true, info: true },
      { text: "환불 페이백", included: true },
    ],
  },
];

const formatCurrency = (n: number) => n.toLocaleString("ko-KR");

export default function PaymentContentV3() {
  const floatingAreaRef = useRef<HTMLDivElement>(null);

  const paddingX = 4.5;

  const [user, setUser] = useRecoilState(userSimpleAtom);
  const { isChecking } = useAutoLoginV3();
  const { isFetching } = useFetchUserV3();
  const navigate = useNavigate();
  const refundLabel = "14일 내 전액 환불 보장";
  const guaranteeLabel = "14일 전액 환불 보장";

  const isFirstOrder = true;
  const fullWidth = true;

  const [countCoachSeatRemain, setCountCoachSeatRemain] = useState<
    number | undefined
  >(undefined);

  const trackPromotion = useMixpanelMKT({
    eventName: "플랜 선택 페이지 진입",
    version: "v3",
  });

  function useEffectOnce(callback: () => void) {
    const ref = useRef(false);

    useEffect(() => {
      if (ref.current) return;
      ref.current = true;

      if (typeof callback === "function") {
        callback();
      }
    }, []);
  }

  useEffectOnce(() => {
    trackPromotion();
  });

  useQuery(
    [PAYMENT_DOMAIN, PAYMENT_COACH_SEAT_COUNT_ENDPOINT],
    () => paymentApi.countRemainCoachSeat(),
    {
      enabled: true,
      refetchInterval: 5000,
      refetchIntervalInBackground: true,
      refetchOnWindowFocus: true,
      onSuccess: (data) => {
        if (data && data.result) {
          // Always update, including 0, so UI reflects real-time count
          setCountCoachSeatRemain(data.result.countRemainCoachSeat ?? 0);
        } else {
          setCountCoachSeatRemain(undefined);
        }
      },
    }
  );

  const SCROLL_CONTAINER_ID = "payment-scroll-container";

  const { companyInfoUrl, privacyPolicyUrl, servicePolicyUrl } = useGetUrls();

  const { homeIndicatorHeight } = useGetDeviceInfo();
  const [containerHeight, setContainerHeight] = useState<number>(
    window.innerHeight - homeIndicatorHeight
  );
  const [floatingAreaHeight, setFloatingAreaHeight] = useState<number>(0);

  useEffect(() => {
    setTimeout(() => {
      setFloatingAreaHeight(floatingAreaRef.current?.offsetHeight || 0);
    }, 100);
  }, [floatingAreaRef.current]);

  /* -----------------------------
     신규: 선택된 플랜 상태/헬퍼
  ------------------------------ */
  const SESSION_SELECTED_PLAN_KEY =
    "payment.selectedPlan.100dayABC.withCommitment";
  const [selectedPlan, setSelectedPlan] = useState<PlanKey>(() => {
    try {
      if (typeof window !== "undefined") {
        const saved = sessionStorage.getItem(SESSION_SELECTED_PLAN_KEY);
        if (
          saved &&
          (saved === "starter" || saved === "challenger" || saved === "master")
        ) {
          return saved as PlanKey;
        }
      }
    } catch {}
    return "challenger";
  });
  useEffect(() => {
    try {
      if (typeof window !== "undefined") {
        sessionStorage.setItem(SESSION_SELECTED_PLAN_KEY, selectedPlan);
      }
    } catch {}
  }, [selectedPlan]);

  const selectedPlanObj = plans.find((p) => p.key === selectedPlan)!;

  type InfoModalData = { title: string; subtitle: string; action: string };

  const INFO_CONTENTS: Record<string, InfoModalData> = {
    "기본 코칭 (주 최대 2회)": {
      title: "기본 코칭",
      subtitle:
        "일주일에 최대 2회, 코치의 피드백과 함께 맞춤 활동지가 제공됩니다. 프로그램 진도도 매주 최대 2회까지 나가게 됩니다.",
      action: "확인",
    },
    "집중 코칭 (일 최대 1회)": {
      title: "집중 코칭",
      subtitle:
        "하루 최대 1회, 코치의 피드백과 함께 맞춤 활동지가 제공됩니다. 프로그램 진도도 매일 최대 1회까지 나가게 됩니다.",
      action: "확인",
    },
    "프리미엄 코칭 (일 최대 2회, 최우선 관리)": {
      title: "프리미엄 코칭",
      subtitle:
        "하루 최대 2회, 코치의 피드백과 함께 맞춤 활동지가 가장 먼저 제공됩니다. 프로그램 진도도 매일 최대 2회까지 나가게 됩니다.",
      action: "확인",
    },
    "심층 정기 검사 & 리포트": {
      title: "심층 정기 검사 & 리포트",
      subtitle:
        "정신건강 기본 항목 외 추가 지표까지 2주마다 정기적으로 측정합니다. 검사 후에는 프로그램과 연계한 분석 리포트가 제공됩니다.",
      action: "확인",
    },
    "기본 정기 검사": {
      title: "기본 정기 검사",
      subtitle: "정신건강 기본 항목을 2주마다 정기적으로 측정합니다.",
      action: "확인",
    },
  };

  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [infoModal, setInfoModal] = useState<InfoModalData>({
    title: "",
    subtitle: "",
    action: "확인",
  });

  const handleOpenInfo = (featureText: string) => {
    const next = INFO_CONTENTS[featureText] || {
      title: featureText,
      subtitle: "자세한 내용은 고객센터로 문의해주세요.",
      action: "확인",
    };
    setInfoModal(next);
    setInfoModalOpen(true);
  };

  const handleOpenInfoCoachSeat = (planName: string) => {
    setInfoModal({
      title: "정원 제한",
      subtitle: `최상의 프로그램 진행 경험을 위해, 코치별 담당 인원을 제한하고 있습니다. 현재 ${planName} 플랜의 정원 마감이 임박하였습니다.`,
      action: "확인",
    });
    setInfoModalOpen(true);
  };

  // 로그인 체크 후 리다이렉트
  useEffect(() => {
    if (!isChecking && !isFetching && !user?.accessToken) {
      navigate(PATH.v3Login, { replace: true });
    }
  }, [isChecking, isFetching, user, navigate]);

  // 로딩 중이거나 user 정보가 없으면 CircularProgress 표시
  if (isChecking || isFetching || !user?.nickname) {
    return (
      <ContentTemplateV2
        noPadding
        isWide
        setContainerHeight={setContainerHeight}
        containerId={SCROLL_CONTAINER_ID}
      >
        <Stack
          sx={{
            width: "100%",
            height: "100vh",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: theme.vars.palette.Background.Level3,
          }}
        >
          <CircularProgress color="neutral" />
        </Stack>
      </ContentTemplateV2>
    );
  }

  return (
    <ContentTemplateV2
      noPadding
      isWide
      setContainerHeight={setContainerHeight}
      containerId={SCROLL_CONTAINER_ID}
    >
      {containerHeight > 0 && (
        <Stack
          sx={{
            pb: "150px",
            backgroundColor: theme.vars.palette.Background.Level3,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              width: "100%",
              px: `8px`,
              position: "fixed",
              top: 0,
              zIndex: 100,
              height: `60px`, // 헤더 높이 설정
              backgroundColor: theme.vars.palette.Background.Level3,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <BackButton onBack={() => navigate(-1)} />
            </Stack>
          </Stack>

          {/* 플랜 선택 */}
          <Stack
            justifyContent="center"
            alignItems={"center"}
            sx={{
              width: "100%",
              maxWidth: "100%",
              mx: "auto",
              px: paddingX,
              pt: "68px", // 헤더 높이만큼 상단 패딩 추가
              pb: 10,
            }}
          >
            <Stack
              sx={{
                width: "100%",
                maxWidth: "660px",
              }}
              spacing={5}
              alignItems="center"
            >
              <Stack spacing={"16px"}>
                <CustomTypography
                  variant={"H2"}
                  sx={{
                    color: theme.vars.palette.Content.Strong,
                    fontWeight: 700,
                    textAlign: "center",
                  }}
                >
                  {`${user?.nickname}님, 100일 완성\n플랜을 선택해주세요`}
                </CustomTypography>
              </Stack>

              <Box
                sx={{
                  width: "100%",
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  gap: "12px",
                }}
              >
                {plans.map((plan) => {
                  const selected = selectedPlan === plan.key;
                  return (
                    <Box
                      key={plan.key}
                      sx={{
                        borderRadius: "16px",
                        backgroundColor: theme.vars.palette.Surface.Forward,
                        px: "24px",
                        py: "28px",
                      }}
                    >
                      <Stack spacing={"20px"}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="flex-start"
                        >
                          <Stack spacing={0.5} sx={{ width: "100%" }}>
                            <Stack
                              direction="row"
                              alignItems="center"
                              justifyContent="space-between"
                              sx={{ width: "100%", pl: 0.5 }}
                            >
                              <CustomTypography
                                variant="Body"
                                sx={{
                                  color: theme.vars.palette.Content.Highlight,
                                  fontWeight: 700,
                                }}
                              >
                                {plan.name}
                              </CustomTypography>
                              {plan.tag && (
                                <Box
                                  sx={{
                                    height: "20px",
                                    borderRadius: "4px",
                                    px: "4px",
                                    display: "flex",
                                    alignItems: "center",
                                    backgroundColor:
                                      plan.tag === "가장 밀도 있는"
                                        ? theme.vars.palette.Surface
                                            .SoftHighlightWithAlert
                                        : theme.vars.palette.Surface
                                            .SoftHighlight,
                                  }}
                                >
                                  <Typography
                                    variant="Footnote"
                                    sx={{
                                      color:
                                        plan.tag === "가장 밀도 있는"
                                          ? theme.vars.palette.Content
                                              .HighlightWithAlert
                                          : theme.vars.palette.Content
                                              .Highlight,
                                      fontWeight: 700,
                                    }}
                                  >
                                    {plan.tag}
                                  </Typography>
                                </Box>
                              )}
                            </Stack>
                            <CustomTypography
                              variant="H3"
                              sx={{
                                color: theme.vars.palette.Content.Strong,
                                fontWeight: 700,
                              }}
                            >
                              {`${formatCurrency(plan.price)}원`}
                              <CustomTypography
                                component="span"
                                variant="Body"
                                sx={{
                                  ml: 0.5,
                                  color: theme.vars.palette.Content.Medium,
                                  fontWeight: 500,
                                }}
                              >
                                {"/ 100일"}
                              </CustomTypography>
                            </CustomTypography>
                          </Stack>
                        </Stack>

                        {/* 선택 버튼 */}
                        <Button
                          fullWidth
                          size="md"
                          variant={selected ? "Outlined" : "Soft"}
                          onClick={() => setSelectedPlan(plan.key)}
                          sx={{ borderRadius: "10px", fontWeight: 700 }}
                        >
                          {selected ? (
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <CheckIcon ariaLabel="selected" />
                              <span>선택됨</span>
                            </span>
                          ) : (
                            "선택하기"
                          )}
                        </Button>

                        {countCoachSeatRemain &&
                          countCoachSeatRemain > 0 &&
                          plan.key !== "starter" && (
                            <Stack
                              direction="row"
                              spacing={"4px"}
                              sx={{
                                mt: "8px !important",
                                width: "100%",
                                justifyContent: "center",
                                alignItems: "center",
                              }}
                            >
                              <Typography
                                variant={"Caption"}
                                sx={{
                                  px: 0,
                                  color: theme.vars.palette.Content.Medium,
                                  fontWeight: 500,
                                }}
                              >
                                {"현재 신청 가능 인원:"}
                                <Typography
                                  variant={"Caption"}
                                  sx={{
                                    pr: 0,
                                    color:
                                      theme.vars.palette.Content
                                        .HighlightWithAlert,
                                    fontWeight: 500,
                                  }}
                                >
                                  {plan.key === "challenger"
                                    ? countCoachSeatRemain + 1
                                    : countCoachSeatRemain}
                                  명
                                </Typography>
                              </Typography>
                              <Box
                                role="button"
                                tabIndex={0}
                                aria-label={`정원 제한 자세히 보기`}
                                onClick={() =>
                                  handleOpenInfoCoachSeat(plan.name)
                                }
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    handleOpenInfoCoachSeat(plan.name);
                                  }
                                }}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  cursor: "pointer",
                                }}
                              >
                                <InfoIcon ariaLabel="info" />
                              </Box>
                            </Stack>
                          )}

                        {/* 혜택 목록 */}
                        <Stack spacing={1}>
                          {(isFirstOrder
                            ? plan.features
                            : plan.features.filter(
                                (feat) => !feat.text.includes("환불")
                              )
                          ).map((f, idx) => (
                            <Stack
                              key={`${plan.key}-${idx}`}
                              direction="row"
                              alignItems="center"
                              justifyContent="space-between"
                              sx={{ width: "100%" }}
                            >
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={0.5}
                              >
                                <CheckIcon ariaLabel="included" />
                                <Typography
                                  variant="Body"
                                  sx={{
                                    px: 0,
                                    color: theme.vars.palette.Content.Strong,
                                    fontWeight: 500,
                                  }}
                                >
                                  {f.text === "환불 페이백"
                                    ? refundLabel
                                    : f.text}
                                </Typography>
                              </Stack>
                              {f.info && (
                                <Box
                                  role="button"
                                  tabIndex={0}
                                  aria-label={`${f.text} 자세히 보기`}
                                  onClick={() => handleOpenInfo(f.text)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      handleOpenInfo(f.text);
                                    }
                                  }}
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    cursor: "pointer",
                                  }}
                                >
                                  <InfoIcon ariaLabel="info" />
                                </Box>
                              )}
                            </Stack>
                          ))}
                        </Stack>
                      </Stack>
                    </Box>
                  );
                })}
              </Box>
            </Stack>
          </Stack>

          {isFirstOrder && (
            <Stack
              py={10}
              justifyContent="center"
              alignItems={"center"}
              sx={{
                backgroundColor: theme.vars.palette.Background.Level3,
                width: "100%",
                mx: "auto",
                px: paddingX,
              }}
            >
              <Stack spacing={5} sx={{ width: "100%", maxWidth: "660px" }}>
                <Typography
                  variant={"H2"}
                  sx={{
                    p: 0,
                    color: theme.vars.palette.Content.Strong,
                    fontWeight: 700,
                  }}
                >
                  {"자주 묻는 질문"}
                </Typography>
                <FAQTypeC />
              </Stack>
            </Stack>
          )}

          <Stack
            spacing={2}
            justifyContent="center"
            alignItems={"center"}
            sx={{
              backgroundColor: theme.vars.palette.Background.Level3,
              width: "100%",
              maxWidth: "660px",
              mx: "auto",
              px: paddingX,
              pt: isFirstOrder ? 0 : 9,
            }}
          >
            <Stack direction={"row"} spacing={3}>
              <LinkV2 text={"사업자 정보"} url={companyInfoUrl} />
              <LinkV2 text={"개인정보 처리방침"} url={privacyPolicyUrl} />
              <LinkV2 text={"서비스 이용약관"} url={servicePolicyUrl} />
            </Stack>
          </Stack>

          <Box>
            <FloatingArea
              fullWidth={fullWidth}
              ref={floatingAreaRef}
              paddingBottom={"12px"}
            >
              <Stack
                spacing={1.5}
                sx={{ width: "100%" }}
                alignItems="center"
                justifyContent={"space-between"}
              >
                {/* 선택된 플랜에 따른 문구 동기화 */}
                <CustomTypography
                  variant={"Caption"}
                  sx={{
                    p: 0,
                    fontWeight: 500,
                    color: theme.vars.palette.Content.Medium,
                    textAlign: "center",
                  }}
                >
                  {`100일 완성 ${formatCurrency(selectedPlanObj.price)}원\u00A0\u00A0|\u00A0\u00A0${guaranteeLabel}`}
                </CustomTypography>

                <Stack
                  spacing={"16px"}
                  sx={{ width: "100%" }}
                  alignItems="center"
                  justifyContent={"space-between"}
                >
                  {<PaymentButtonV3 selectedPlan={selectedPlan} />}
                </Stack>
              </Stack>
            </FloatingArea>
          </Box>
        </Stack>
      )}
      <CustomModal
        open={infoModalOpen}
        onClose={() => setInfoModalOpen(false)}
        title={infoModal.title}
        subtitle={infoModal.subtitle}
        actions={[
          {
            label: infoModal.action,
            onClick: () => setInfoModalOpen(false),
            variant: "Solid",
          },
        ]}
      />
    </ContentTemplateV2>
  );
}
